/**
 * SEO ENGINE FILE - single source of truth for the site's identity, URL, and
 * indexing switch. Everything SEO-related (canonicals, Open Graph, sitemap,
 * robots, schema) is built from the values here.
 *
 * 🔒 Do NOT let the AI builder (Lovable) edit this file. See AGENTS.md.
 *
 * ⛑️  Phase 0 rule: values marked `TODO` must be filled with REAL business
 *     facts from the owner. NEVER invent a value. Leave it blank until
 *     confirmed - the site stays `noindex` until the facts are real.
 */

/* -------------------------------------------------------------------------- */
/*  Domain + indexing (driven by build-time env)                              */
/* -------------------------------------------------------------------------- */

// NOTE: these must be referenced as literal `import.meta.env.VITE_*` so Vite
// can inline them into the client bundle. Do not alias `import.meta.env`.
const ENV_SITE_URL = (import.meta.env.VITE_SITE_URL as string | undefined)?.trim();
const ENV_INDEXABLE = (import.meta.env.VITE_INDEXABLE as string | undefined)?.trim();

/** Real production domain. Override per deploy with `VITE_SITE_URL`. */
const RAW_SITE_URL = ENV_SITE_URL || "https://compasslenders.com"; // confirmed production domain

/** Canonical origin, never with a trailing slash. Build every URL from this. */
export const SITE_URL = RAW_SITE_URL.replace(/\/+$/, "");

/**
 * The indexing switch. Google may ONLY index the site when this is `true`.
 * Turn it on (set `VITE_INDEXABLE=true`) only on the real production build,
 * and only after Phases 0-3 are complete. Default OFF -> every page emits
 * `noindex` and `/robots.txt` returns `Disallow: /`.
 */
export const INDEXABLE = ENV_INDEXABLE === "true";

/** Build an absolute URL (`https://domain/path`) from a site-relative path. */
export function absoluteUrl(path = "/"): string {
  if (/^https?:\/\//i.test(path)) return path;
  let p = path.startsWith("/") ? path : `/${path}`;
  // Match the router's no-trailing-slash normalization so canonicals/sitemap
  // URLs never point at a 307 redirect.
  if (p.length > 1) p = p.replace(/\/+$/, "");
  return `${SITE_URL}${p}`;
}

/* -------------------------------------------------------------------------- */
/*  Business identity (NAP, hours, licensing) - REAL values only              */
/* -------------------------------------------------------------------------- */

export type OpeningHours = {
  /** schema.org DayOfWeek names, e.g. "Monday". */
  days: string[];
  /** 24h "HH:MM". */
  opens: string;
  /** 24h "HH:MM". */
  closes: string;
};

export const SITE = {
  /** Brand name shown to users. */
  name: "Compass Lenders",
  /** Short brand label used in tight UI. */
  short: "Compass",
  /** Exact registered legal entity. */
  legalName: "Compass Lenders", // confirmed by owner - no "LLC" suffix claimed
  /** One-line description of the business. */
  description:
    "Independent business financing advisory arranging working capital, SBA, equipment, and specialty loans for Dallas-Fort Worth businesses.",

  /* --- Contact (REAL only) --- */
  phone: "(469) 213-1499",
  phoneE164: "+14692131499",
  email: "", // owner: email not used as a public contact channel - leave blank

  /* --- Real public office (confirmed by owner) --- */
  address: {
    street: "8100 Lomo Alto Dr",
    locality: "Dallas",
    region: "TX",
    postalCode: "75225",
    country: "US",
  },
  /** Map coordinates for the office (null keeps geo out of schema). */
  geo: {
    latitude: 32.861321 as number | null, // geocoded from 8100 Lomo Alto Dr, Dallas, TX 75225 (OpenStreetMap)
    longitude: -96.810978 as number | null,
  },

  /** State lending / broker license. */
  licenseNumber: "", // owner: no license number available - keep blank, make NO "licensed" claims on-site

  /* --- Opening hours --- */
  openingHours: [
    { days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "08:00", closes: "19:00" },
    { days: ["Saturday"], opens: "09:00", closes: "14:00" },
  ] as OpeningHours[],
  /** Human-readable hours for the UI. */
  hoursHuman: "Mon-Fri · 8AM - 7PM CT",

  /** Audience / market label used in body copy. */
  areaLabel: "Dallas-Fort Worth small and mid-sized businesses",

  /** Cities served - feeds `areaServed` schema. Keep ≤ 20 (GBP service-area cap). */
  citiesServed: [
    "Dallas",
    "Fort Worth",
    "Plano",
    "Irving",
    "Garland",
    "Frisco",
    "McKinney",
    "Grand Prairie",
    "Mesquite",
    "Carrollton",
    "Richardson",
    "Denton",
    "Arlington",
    "Lewisville",
    "Coppell",
    "Addison",
    "Duncanville",
    "Lancaster",
  ],

  /** Real social / profile links (Phase 0) - feeds schema `sameAs`. */
  sameAs: [] as string[], // owner: no public social/profile URLs available yet

  /** Named content reviewer/author for E-E-A-T. Blank name -> the page credits the advisory team. */
  reviewer: {
    name: "", // TODO: real person's name if the owner designates a content author/reviewer
    title: "", // e.g. "Managing Advisor"
    credential: "", // e.g. "12+ years arranging business financing"
  },
};

/** Single-line postal address for the footer / UI. */
export const ADDRESS_LINE = `${SITE.address.street}, ${SITE.address.locality}, ${SITE.address.region} ${SITE.address.postalCode}`;
